import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Typography,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ReloadOutlined, FileAddOutlined } from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_LIST_FIELD_COLLECTION,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import UpdateFieldCollectionDoc from "./modal/UpdateFieldCollectionDoc";

const { Option } = Select;
const { Text } = Typography;

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD MMM YY") : "-";

const getCaseTypeLabel = (r) => {
  if (r.flagLoanNew === 1) return "ทำยอม";
  if (r.judgeType) return r.judgeType;
  return "ยังไม่มีคำพิพากษา";
};

// ── แปลข้อความสถานะดิบจากระบบให้เป็นคำที่ผู้ใช้เข้าใจง่าย ──────
const STATUS_DESCRIPTION_LABELS = {
  "ceo อนุมัติ": "เกิดสัญญาใหม่แล้ว",
};

const getStatusDescriptionLabel = (statusDescription) =>
  STATUS_DESCRIPTION_LABELS[statusDescription] || statusDescription;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState();
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_LIST_FIELD_COLLECTION,
        { userId: userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );
      console.log("res.data.data", res.data.data);

      if (res.data.data && res.data.data.length > 0) {
        const sortedData = [...res.data.data]
          .sort((a, b) => {
            const timeA = a.assignDate ? dayjs(a.assignDate).valueOf() : 0;
            const timeB = b.assignDate ? dayjs(b.assignDate).valueOf() : 0;
            return timeA - timeB;
          })
          .map((item, index) => ({
            ...item,
            key: item.CONTNO || item.id || `row-${index}`,
          }));
        setDataArr(sortedData);
      } else {
        setDataArr([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
      }
    } finally {
      setLoading(false);
    }

    return () => {
      controller.abort();
    };
  }, [userId]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select (แบ่งตามสถานะเอกสาร docEnforce/docSeize)
  const statusOptions = useMemo(
    () => [
      { value: "all", icon: "🗂️", label: "ทั้งหมด" },
      { value: "waitEnforce", icon: "🕒", label: "รอเอกสารบังคับคดี" },
      { value: "waitSeize", icon: "📋", label: "รอเอกสารยึดทรัพย์" },
      { value: "done", icon: "✅", label: "ครบถ้วน" },
    ],
    [],
  );

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      let passStatus = true;
      if (processStatus === "waitEnforce") {
        passStatus = !item.docEnforce;
      } else if (processStatus === "waitSeize") {
        passStatus = !!item.docEnforce && !item.docSeize;
      } else if (processStatus === "done") {
        passStatus = !!item.docEnforce && !!item.docSeize;
      }

      return passQuery && passStatus;
    });
  }, [dataArr, searchInputTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus("all");
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  // ✅ Reload หลังบันทึกสำเร็จ
  const handleReload = useCallback(() => {
    loadMain();
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สัญญา",
      key: "contract",
      align: "left",
      width: 200,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text copyable={!!r.contno} strong>
              {r.contno || "-"}
            </Text>
          </div>
          {r.newContno && (
            <div style={{ color: "#888" }}>สัญญาใหม่ {r.newContno}</div>
          )}
          <div style={{ color: "#888" }}>เจ้าของงาน {r.ownerName || "-"}</div>
        </div>
      ),
    },
    {
      title: "คดี / ทนายความ",
      key: "case",
      align: "left",
      width: 210,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <span style={{ color: "#888" }}>เลขคดีดำ </span>
            {r.blackCaseNumber || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>เลขคดีแดง </span>
            {r.redCaseNumber || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>ทนายความ </span>
            {r.lawyerName || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "ประเภท / สถานะงาน",
      key: "stage",
      align: "left",
      width: 260,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Tag color={r.flagLoanNew === 1 ? "gold" : "blue"}>
            {getCaseTypeLabel(r)}
          </Tag>
          <div style={{ color: "#595959", marginTop: 4 }}>{r.stage || "-"}</div>
          {r.statusDescription && (
            <div style={{ color: "#888" }}>
              สถานะ: {getStatusDescriptionLabel(r.statusDescription)}
            </div>
          )}
        </div>
      ),
    },
    //     export const OPTIONS_STATUS = [ //สถานะทรัพย์
    //   { value: 0, label: "ยังไม่เคยสืบทรัพย์" },
    //   { value: 1, label: "แจ้งธรุการส่งสืบทรัพย์" },
    //   { value: 2, label: "ดำเนินการส่งสืบทรัพย์" },
    //   { value: 3, label: "เจอทรัพย์" },
    //   { value: 4, label: "ไม่เจอทรัพย์" },
    // ];
    // {
    //   title: "สืบทรัพย์",
    //   key: "investigate",
    //   align: "left",
    //   width: 180,
    //   render: (_, r) => (
    //     <div style={{ fontSize: 13, lineHeight: 1.7 }}>
    //       <div style={{ color: "#888" }}>
    //         ส่งสืบ: {formatThaiDate(r.investigateSendDate)}
    //       </div>
    //       <div style={{ color: "#888" }}>
    //         ตอบกลับ: {formatThaiDate(r.investigateResponseDate)}
    //       </div>
    //       {r.investigateStatus != null && (
    //         <div style={{ color: "#888" }}>สถานะ: {r.investigateStatus}</div>
    //       )}
    //     </div>
    //   ),
    // },
    {
      title: "บังคับคดี",
      key: "enforce",
      align: "left",
      width: 240,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Space size={4} wrap>
            <Tag color={r.docEnforce ? "green" : "default"}>หมายบังคับคดี</Tag>
            <Tag color={r.docSeize ? "green" : "default"}>หมายยึดทรัพย์</Tag>
          </Space>
          {r.enforceCaseDate && (
            <div style={{ color: "#888" }}>
              ตั้งเรื่อง: {formatThaiDate(r.enforceCaseDate)}
            </div>
          )}
          {r.enforceReceiveName && (
            <div style={{ color: "#888" }}>
              ผู้รับ: {r.enforceReceiveName}
              <br />
              รับงาน {formatThaiDate(r.enforceReceiveDate)}
            </div>
          )}
          {r.impoundDate && (
            <div style={{ color: "#888" }}>
              ยึดทรัพย์: {formatThaiDate(r.impoundDate)}
            </div>
          )}
          {r.saleDate && (
            <div style={{ color: "#888" }}>
              ขายทอดตลาด: {formatThaiDate(r.saleDate)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "สถานะสัญญา",
      key: "contractStatus",
      align: "left",
      width: 200,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Tag color={r.flagActive ? "green" : "red"}>
            {r.flagActive ? "ดำเนินการอยู่" : "จบเคส"}
          </Tag>
        </div>
      ),
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        return (
          <Button
            type="primary"
            ghost
            icon={<FileAddOutlined />}
            onClick={() => {
              setDataRecord(record);
              setIsModal(true);
            }}
          >
            เพิ่มไฟล์เอกสาร
          </Button>
        );
      },
    },
  ];

  return (
    <Card>
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Row gutter={[0, 16]}>
          {/* Filters */}
          <Col span={24}>
            <Space size={10} wrap>
              <Select
                size="large"
                style={{ minWidth: 180 }}
                placeholder="เลือกสถานะ"
                value={processStatus}
                onChange={setProcessStatus}
              >
                {statusOptions.map(({ value, icon, label }) => (
                  <Option key={value} value={value}>
                    <span style={{ marginRight: 8 }}>{icon}</span>
                    {label}
                  </Option>
                ))}
              </Select>

              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ"
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span style={{ marginRight: 5 }}>🔍</span>}
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                size="large"
                style={{
                  borderRadius: 8,
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffa39e",
                  color: "#cf1322",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </Space>
          </Col>

          {/* Table */}
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={arrayTable}
              rowKey="key"
              scroll={{ x: 1000 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              footer={() => (
                <div style={{ textAlign: "left", fontWeight: 500 }}>
                  จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                </div>
              )}
            />
          </Col>
        </Row>
      </Spin>

      {isModal && (
        <UpdateFieldCollectionDoc
          open={isModal}
          onCancel={setIsModal}
          dataDefault={dataRecord}
          onSuccess={handleReload}
        />
      )}
    </Card>
  );
};

const MainFieldCollectionDoc = MotionHoc(Main);
export default MainFieldCollectionDoc;
